/* eslint-disable flowtype/require-return-type */
import Radio from "backbone.radio";
import { escape, result } from "lodash-es";
import { captureMessage, captureBreadcrumb } from "extensions/sentry";
import lpad from "lodash-es/padStart";
import humanize from "lodash-es/startCase";
import moment from "moment";

import strings from "common/strings";
import { store } from "common/store";
import { weekToText } from "common/utils";

import { products } from "common/routers/products";
import { getters } from "common/components/getters/getters";

import { Qualifiers } from "common/components/queryBuilder/qualifiers";

import { mcc_categories } from "./mcc_categories";

// List of supported mappings
const mappings = {
    test_auth: "auths_tests",
    retries: "auths_attempts",
    processor: "provider",
    settled_processor: "provider",
    method: "payment_method",
    currency: "currency_alias",
    country: "issuing_country",
    terminal_country: "issuing_country",
    business_unit: "bu",
    merchant_ref: "merchant",
    settle_date: "date_time",
    accepted_date: "date_time"
};

const computedMccCategories = mcc_categories.reduce((memo, { category, lower, upper }) => ({
    ...memo,
    [category]: `${lpad(lower, 4, "0")}-${lpad(upper, 4, "0")} ${category}`
}), {});

const all_products = Object.values(products).reduce((memo, p) => ({
    ...memo,
    [p]: p
}), {});

export const references = {
    fromQualifiers(str) {
        const q = new Qualifiers({
            value: str
        }).toObject();
    
        return Object.entries(q).reduce((memo, [category, values]) => [
            ...memo,
            ...values.map((v) => references.toAlias(v.replaceAll("&quot;", "\""), category))
        ], []).sort().join(", ");
    },
    
    getMatchFromGroup(ref, aliases, groupType, options) {
        const getRefFromAliasDefault = () => aliases[ref];
        const humanizeDates = () => {
            if (/^\d{4}-\d{2}-\d{2}$/.test(ref)) { // 2019-12-12
                return moment.utc(ref).format("MMM D, YYYY");
            }
            
            if (/^\d{4}-\d{2}$/.test(ref)) { // 2019-12
                return moment.utc(ref).format("MMMM YYYY");
            }
            
            if (/^\d{4}-W\d{2}$/.test(ref)) { // 2019-W28
                return weekToText(ref, true, true).replace("<br/>", ", ");
            }
            
            const momentizedDate = moment.utc(ref);
            if (momentizedDate.isValid()) {
                // Any other type of date_time
                return momentizedDate.format("MMM D, YYYY");
            }
            
            return ref;
        };

        const groupTypes = {
            merchant() {
                return aliases.find(({ id }) => id === ref)?.display_name;
            },
            provider() {
                return aliases.find(({ name }) => name === ref)?.alias;
            },
            acquirer: getRefFromAliasDefault,
            provider_path() {
                return JSON.parse(ref)
                    .map((p) => aliases.find(({ name }) => name === p)?.alias || p) // in case processor that is not in aliases
                    .join(" > ");
            },
            reason_code() {
                if (["others", "unknown"].includes(ref.toLowerCase())) {
                    return ref;
                }
                
                return aliases[ref]?.description;
            },
            response_code() {
                return aliases[ref]?.description;
            },
            currency_alias: () => ref,
            payment_method: getRefFromAliasDefault,
            issuing_country: getRefFromAliasDefault,
            
            mcc: () => {
                if (ref.toLowerCase() === "unknown" || ref.toLowerCase() === "unknown mcc" || ref === "") {
                    return ref;
                }
                
                if (aliases[ref]) return aliases[ref].description;
                
                if (ref === "Other MCCs used by your peers") {
                    return ref;
                }
    
                // If we can't find MCC display name in our set of references then captureException and return a ref
                captureMessage("Error converting MCC to display name", {
                    level: "warning",
                    extra: {
                        mcc: ref
                    }
                });
                
                return ref;
            },
            mcc_category: getRefFromAliasDefault,
            bu: () => {
                if (options.full) {
                    return aliases[ref];
                }
                
                if (ref.toLowerCase() === "unknown") return ref;
                
                return aliases[ref].display_name;
            },
            mid: getRefFromAliasDefault,
            cycle: () => {
                const opts = {
                    method: "all",
                    ...options
                };
                
                // eslint-disable-next-line eqeqeq
                if (aliases[opts.method] == null) {
                    opts.method = "all";
                }
                
                return aliases[opts.method][ref];
            },
            auths_attempts: getRefFromAliasDefault,
            auths_tests: getRefFromAliasDefault,
            category: getRefFromAliasDefault,
            attempt() {
                let match;
                
                // $FlowFixMe: flow doesn't understand ~~ conversion here. Suppressing this error until we can refactor
                if (~~ref === 0) { // Captures non-integer things like: "3rd attempt", "initial attempt"
                    match = ref;
                    // $FlowFixMe: flow doesn't understand ~~ conversion here. Suppressing this error until we can refactor
                } else if (~~ref === 1) { // Captures both 1 && "1"
                    match = "Initial attempt";
                } else if (typeof aliases === "function") { // The rest
                    match = `${aliases(ref)} retry`;
                }
                
                return match;
            },
            response_type: () => {
                if (ref === "soft") {
                    return aliases.soft_decline;
                }
                
                if (ref === "hard") {
                    return aliases.hard_decline;
                }
                
                return aliases[ref];
            },
            stl_type: getRefFromAliasDefault,
            stl_status: getRefFromAliasDefault,
            fee_type: getRefFromAliasDefault,
            cvv2_response_code: getRefFromAliasDefault,
            avs_response_code: getRefFromAliasDefault,
            psd2: getRefFromAliasDefault,
            fraud_program: getRefFromAliasDefault,
            fraud_reason_code: getRefFromAliasDefault,
            fraud_response: getRefFromAliasDefault,
            original_fraud_response: getRefFromAliasDefault,
            digital_wallet: getRefFromAliasDefault,
            EUR_amount_bucket: getRefFromAliasDefault,
            psd2_region: getRefFromAliasDefault,
            product: getRefFromAliasDefault,
            transaction_src: humanize.bind(this, ref),
            package() {
                const match = aliases.find(({ _id }) => _id === ref);
                
                // Return a new object to avoid changing values by reference
                return {
                    ...match
                };
            },
            is_mimicked: () => {
                if (ref === true || ref === "true") return "Mimicked";
                return "Not mimicked";
            },
            is_superuser: () => {
                if (ref === true || ref === "true") return "Superuser";
                return "Not superuser";
            },
            is_wp_user: () => {
                // eslint-disable-next-line sentence-case/sentence-case
                if (ref === true || ref === "true") return "Worldpay RM";
                // eslint-disable-next-line sentence-case/sentence-case
                return "Not Worldpay RM";
            },
            date_time: humanizeDates,
            "activity_date.day": humanizeDates,
            "settle_date.day": humanizeDates,
            "original_txn.activity_date.day": humanizeDates,
            status: getRefFromAliasDefault
        };
        
        if (groupType === "reason_code") {
            return groupTypes.reason_code();
        }
        return groupTypes[mappings[groupType] || groupType]();
    },
    
    getAliasGroupByType(type, options = {}) {
        const state = store.getState();
        
        const aliases = {
            merchant: () => store.getState().common.all_merchants,
            provider: () => store.getState().common.merchant_config.processors,
            acquirer: () => {
                const dimensionAcquirers = store.getState().common.merchant_dimensions.data?.acquirer || [];
                const configAcquirers = store.getState().common.merchant_config.processors || [];
                return configAcquirers.reduce((memo, provider) => {
                    if (dimensionAcquirers.includes(provider.name)) {
                        return {
                            ...memo,
                            [provider.name]: provider.alias
                        };
                    }
                    return memo;
                }, {});
            },
            provider_path: () => store.getState().common.merchant_config.processors,
            bu: () => {
                const entities = Radio.channel("main").request("get:user")?.entities;
                
                if (!entities) return {};
    
                // Go through all entities that the user has access to, and find tree of selected entity
                const tree = entities.findNode(store.getState().common.bu_ref || store.getState().common.merchant_ref).getAll(true);
    
                // Remove very first item as it is the highest possible node (All levels)
                tree.shift();
    
                return tree.reduce((memo, i) => {
                    memo[i.id] = {
                        display_name: escape(i.display_name),
                        parent: i.parent
                    };
                    return memo;
                }, Object.create(null));
            },
            reason_code: () => state.common.mappings.cbk_codes,
            response_code: () => state.common.mappings.decline_codes,
            payment_method: () => state.common.mappings.payment_methods,
            currency_alias: () => state.common.mappings.currencies,
            issuing_country: () => state.common.mappings.countries,
            "package": () => state.common.mappings.packages,
            mcc: () => state.common.mappings.mcc,
            cycle: () => ({
                all: state.common.mappings.cbk_lifecycle,
                ...strings.chargeback_states
            }),
            category: ["Fraud", "Technical", "Clerical", "Quality", "Other"].reduce((acc, category) => ({
                ...acc,
                [category]: category
            }), {}),
            auths_attempts: {
                initials: strings.analytics.authorizations.initialAttempts,
                all: strings.analytics.authorizations.allAttempts,
                retries: strings.analytics.authorizations.retriesAttempts
            },
            auths_tests: {
                include: strings.auths_tests_include,
                exclude: strings.auths_tests_exclude,
                only: strings.auths_tests_only
            },
            attempt: () => (n) => {
                const num = n - 1;
                const s = ["th", "st", "nd", "rd"];
                const v = num % 100;
                
                return num + (s[(v - 20) % 10] || s[v] || s[0]);
            },
            mid: Object.create(null), // () => store.getState().common.merchant_dimensions.data?.mid || [], - supposed to be an object, not a list
            transaction_src: () => store.getState().common.merchant_dimensions.data?.transaction_src || [],
            response_type: {
                approval: "Approvals",
                soft_decline: "Declines (soft)",
                hard_decline: "Declines (hard)",
                reject: "Rejects",
                unknown: "Unknown"
            },
            stl_type: {
                sale: strings.sale,
                refund: strings.refund,
                OCT: "Payout"
            },
            fee_type: {
                processor: strings.analytics.feesPage.processorFees,
                assessment: strings.analytics.feesPage.assessmentFees,
                interchange: strings.analytics.feesPage.interchangeFees
            },
            get stl_status() {
                const base = {
                    settled: "Settled",
                    rejected: "Rejected",
                    pending: "Pending"
                };
                
                if (store.getState().common.merchant_capabilities.has_accepted_stl_status) {
                    return {
                        ...base,
                        accepted: "Accepted"
                    };
                }
                
                return base;
            },
            psd2: {
                eligible: "In scope",
                exempt: "Out of scope"
            },
            psd2_region: {
                // eslint-disable-next-line sentence-case/sentence-case
                EU: "European Union",
                // eslint-disable-next-line sentence-case/sentence-case
                UK: "United Kingdom",
                "N/A": "Not applicable",
                "Unknown psd2 region": "Not reported by provider"
            },
            fraud_reason_code: () => state.common.mappings.fraud_response_codes,
            fraud_response: () => state.common.mappings.fraud_response,
            original_fraud_response: () => state.common.mappings.original_fraud_response,
            fraud_program: () => state.common.mappings.fraud_program,
            digital_wallet: () => state.common.mappings.digital_wallet,
    
            EUR_amount_bucket: {
                "0.00 - 99.99": "0.00 - 99.99",
                "100.00 - 249.99": "100.00 - 249.99",
                "250.00 - 499.99": "250.00 - 499.99",
                "500.00+": "500.00+"
            },
            mcc_category: computedMccCategories,
            avs_response_code: () => state.common.mappings.avs_response_codes,
            cvv2_response_code: () => state.common.mappings.cvv_response_codes,
            product: () => all_products,
            is_mimicked: {
                "true": "Mimicked",
                "false": "Not mimicked"
            },
            is_superuser: {
                "true": "Superuser",
                "false": "Not a superuser"
            },
            is_wp_user: {
                // eslint-disable-next-line sentence-case/sentence-case
                "true": "Worldpay RM",
                // eslint-disable-next-line sentence-case/sentence-case
                "false": "Not a Worldpay RM"
            },
            date_time: {}, // Passing an empty object here to enable lookup for this key
            "activity_date.day": {}, // Passing an empty object here to enable lookup for this key
            "settle_date.day": {}, // Passing an empty object here to enable lookup for this key
            "original_txn.activity_date.day": {}, // Passing an empty object here to enable lookup for this key
            status: { // Aliases for sdr status enums
                pending: "Pending",
                generating: "In-process",
                generated: "Complete",
                delivering: "Delivering",
                delivered: "Delivered"
            }
        };
        // Return null if we don't have requested group
        return result(aliases, mappings[type] || type, null);
    },
    
    toAlias(ref, type, options = {}) {
        // We should never error on converting a reference to alias.
        // It's better to just return the reference than crash.
        // Put a message to sentry to track
        try {
            // Return earlier if ref is null or undefined
            if (ref === null || typeof ref === "undefined") return ref;
            
            // Support getters as a type
            const getter = getters.findById(type);
            if (getter) {
                const alternativeMappings = { // Some transaction objects (fraud) have wrong
                    "5c4f2b289c172b0baf181818": "fraud_reason_code",
                    "5db9d5a782d84c6c839df36f": "processor"
                };
                
                type = alternativeMappings[getter._id] || getter.key;
            }
            
            const aliases = references.getAliasGroupByType(type, options);
            // Just echo back the references if we don't have aliases or matches for this ref type
            if (!aliases) {
                return escape(ref);
            }
    
            return references.getMatchFromGroup(ref, aliases, type, options) || escape(ref);
        } catch (error) {
            console.error(error);
            captureBreadcrumb({
                message: "Error converting a reference to an alias",
                category: "references",
                level: "warning",
                data: {
                    ref,
                    type,
                    name: error.name,
                    message: error.message
                }
            });
            
            return ref;
        }
    }
};
